<template>
  <canvas ref="canvas"
          class="canvas"
          :style="{
            left: left + 'px',
            top: top + 'px',
            width: width + 'px',
            height: height + 'px'
          }"
          @click="onCanvasClick">
    <slot></slot>
  </canvas>
</template>

<script>
import browser from '../../libs/browser'

const isMobile = browser.isMobile
const pixelRatio = Math.min(2, window.devicePixelRatio)

function multiplyPixelRatio (value) {
  return value * pixelRatio
}

function getColor (color) {
  return 'rgb(' + color[0] + ', ' + color[1] + ', ' + color[2] + ')'
}

function getEventPoint (e) {
  if (e.touches) {
    e = e.touches[0] || e.changedTouches[0]
  }
  return [e.pageX || e.clientX, e.pageY || e.clientY]
}

const createAnimator = (function () {
  const animators = []
  const fps = 60
  const duration = 0.23
  const timerDelay = 1000 / fps
  const easing = (t, b, c, d) => {
    return -c * (t /= d) * (t - 2) + b
  }

  let timer = null
  let numAnimators = 0

  function addAnimator (animator) {
    animators.push(animator)
    numAnimators = animators.length
    action()
  }

  function removeAnimator (animator) {
    animators.splice(animators.indexOf(animator), 1)
    numAnimators = animators.length
  }

  function action () {
    cancelAnimationFrame(timer)
    for (let i = 0; i < numAnimators; i++) {
      animators[i].ticker()
    }
    if (numAnimators) {
      timer = requestAnimationFrame(action)
    }
  }

  function getValue (from, to, step, totalStep) {
    return parseInt(easing(step, from, to - from, totalStep))
  }

  function getTime () {
    return new Date().getTime()
  }

  return function (onAnimation, onTick) {
    let tweens = []
    let currentFrame = 0
    let startTime = 0

    function ticker () {
      currentFrame = Math.floor((getTime() - startTime) / timerDelay)

      const setted = setProperties(currentFrame)
      if (setted) {
        onAnimation && onAnimation()
      } else if (currentFrame) {
        currentFrame = 0
        tweens = []
      }

      onTick && onTick()
    }

    function setProperties (step) {
      let setted = false
      const numTweens = tweens.length
      for (let tween, myFrames, myStep, i = 0; i < numTweens; i++) {
        tween = tweens[i]
        if (step >= tween.startFrame && !tween.dead) {
          myFrames = tween.totalFrames - 1
          myStep = Math.min(myFrames, step - tween.startFrame)
          if (myStep > -1) {
            for (let j = 0, numValues = tween.valueFrom.length; j < numValues; j++) {
              tween.target[j] = getValue(tween.valueFrom[j], tween.valueTo[j], myStep, myFrames)
            }
            if (myStep === myFrames) {
              tween.dead = true
            }
            setted = true
          }
        }
      }
      return setted
    }

    function animator (target, to) {
      const numFrames = Math.round(fps * duration)

      const tween = {
        target: target,
        valueFrom: target.slice(),
        valueTo: to,
        startFrame: currentFrame,
        totalFrames: numFrames,
        dead: false
      }

      let existIndex = -1
      for (let i = 0, numTweens = tweens.length; i < numTweens; i++) {
        if (tweens[i].target === target) {
          existIndex = i
          break
        }
      }

      if (existIndex > -1) {
        tweens[existIndex] = tween
      } else {
        tweens.push(tween)
      }

      if (!currentFrame) {
        startTime = getTime()
      }
    }

    function destroy () {
      removeAnimator(animator)
      tweens = null
      return null
    }

    animator.destroy = destroy
    animator.ticker = ticker

    addAnimator(animator)

    return animator
  }
})()

export default {
  name: 'BaTrendPathCanvas',
  components: {
  },
  props: {
    left: {
      type: Number,
      required: true
    },
    top: {
      type: Number,
      required: true
    },
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    connectionStrength: {
      type: Number,
      required: true
    },
    paneScale: {
      type: Number,
      required: true
    },
    panePosition: {
      type: Array,
      required: true
    },
    highlightedSequenceAndNodeIndex: {
      type: String,
      required: true
    }
  },
  data () {
    return {
    }
  },
  watch: {
    width () {
      this.draw(true)
    },
    panePosition (position) {
      if (this._$hoveredChild) {
        this.onCanvasPointerLeave()
      }
    },
    highlightedSequenceAndNodeIndex (index) {
      this.$nextTick(() => {
        const $children = this.$children
        const numChildren = $children.length
        if (index) {
          for (let $child, i = 0; i < numChildren; i++) {
            $child = $children[i]
            if ($child.isThruPath) {
              $child._color = [184, 192, 8, 0]
            }
            this._animate($child._color, [184, 192, 8, $child.isThruPath ? 100 : 18])
          }
        } else {
          for (let $child, i = 0; i < numChildren; i++) {
            $child = $children[i]
            this._animate($child._color, [184, 192, 8, 40])
          }
        }
      })
    }
  },
  mounted () {
    this._$canvas = this.$refs.canvas
    this._context = this._$canvas.getContext('2d')
    this._$offCanvas = document.createElement('canvas')
    this._offContext = this._$offCanvas.getContext('2d')
    this._animate = createAnimator(this.onAnimationUpdate, this.draw)
    if (!isMobile) {
      this._$canvas.addEventListener('mousemove', this.onCanvasPointerMove)
    } else {
      this._$canvas.addEventListener('touchstart', this.onCanvasPointerMove)
    }
    this._$canvas.addEventListener('mouseleave', this.onCanvasPointerLeave)
    this.requestDraw()
  },
  updated () {
    this.requestDraw()
  },
  beforeDestroy () {
    this._animate = this._animate.destroy()
  },
  methods: {
    draw (_forced) {
      const forced = _forced === true || this._forceDraw
      if (forced === true || this._drawRequested) {
        this._drawRequested = false
        this._forceDraw = false

        const $canvas = this.$refs.canvas
        const context = this._context
        const $children = this.$children
        const numChildren = $children.length
        const pointerX = this._pointerX
        const pointerY = this._pointerY

        let $hoveredChild = null
        let needToDraw = forced === true

        context.lineWidth = multiplyPixelRatio(5)

        for (let $child, i = 0; i < numChildren; i++) {
          $child = $children[i]
          let pathDescription = $child.pathDescription
          if ($child._pathDescription !== pathDescription) {
            $child._pathDescription = pathDescription
            $child._path2d = new Path2D(pathDescription)
            needToDraw = true
          }
          if (!$child._color) {
            $child._color = [184, 192, 8, $child.isThruPath ? 100 : this.highlightedSequenceAndNodeIndex ? 18 : 40]
          }
          if (!$child.isThruPath && (context.isPointInPath($child._path2d, pointerX, pointerY) || context.isPointInStroke($child._path2d, pointerX, pointerY))) {
            $hoveredChild = $child
          }
        }

        if ($hoveredChild) {
          if (this._$hoveredChild !== $hoveredChild) {
            if (this._$hoveredChild) {
              this.setLeave(this._$hoveredChild, true)
            }
            this._$hoveredChild = $hoveredChild
            this.setHover(this._$hoveredChild)
            needToDraw = true
          }
          $canvas.classList.add('hovered')
          $canvas.setAttribute('data-id', $hoveredChild.id.replace('path-', ''))
        } else if (this._$hoveredChild) {
          $canvas.classList.remove('hovered')
          $canvas.setAttribute('data-id', '')
          this.setLeave(this._$hoveredChild, true)
          needToDraw = true
        }

        if (needToDraw) {
          $canvas.width = multiplyPixelRatio(this.width)
          $canvas.height = multiplyPixelRatio(this.height)

          context.lineWidth = multiplyPixelRatio(1)

          for (let $child, color, i = 0; i < numChildren; i++) {
            $child = $children[i]
            if ($child._color) {
              context.globalAlpha = $child._color[3] / 100

              if ($child.isInterrupted && !$child.isActive) {
                const gradient = context.createLinearGradient(
                  0, 0, multiplyPixelRatio(this.width), 0
                )
                color = $child._color
                const rgba = 'rgba(' + color[0] + ', ' + color[1] + ', ' + color[2] + ', '
                gradient.addColorStop(0.08, rgba + color[3] + ')')
                gradient.addColorStop(0.13, rgba + '0)')
                gradient.addColorStop(0.87, rgba + '0)')
                gradient.addColorStop(0.92, rgba + color[3] + ')')
                color = gradient
              } else {
                color = getColor($child._color)
              }
              context.fillStyle = context.strokeStyle = color
              context.fill($child._path2d)
              context.stroke($child._path2d)
            }
          }
        }
      }
    },
    getSyncedThruPath (id) {
      id = id.replace('path-', '')
      const $children = this.$children
      const numChildren = $children.length
      for (let i = 0; i < numChildren; i++) {
        if ($children[i].id.indexOf('thrupath-' + id) > -1) {
          return $children[i]
        }
      }
      return null
    },
    setHover ($child) {
      if ($child.hasThruPath) {
        const $thruChild = this.getSyncedThruPath($child.id)
        this._animate($thruChild._color, [117, 179, 254, 100])
        this._animate($child._color, [117, 179, 254, 40])
      } else {
        this._animate($child._color, [117, 179, 254, this.highlightedSequenceAndNodeIndex ? 40 : 100])
      }
    },
    setLeave ($child, withClearHoveredChild) {
      if (withClearHoveredChild) {
        this._$hoveredChild = null
        isMobile && this.$emit('onCloseInfoLayer')
      }
      if (((isMobile && withClearHoveredChild) || !$child.isActive) && $child !== this._$hoveredChild) {
        if ($child.hasThruPath) {
          const $thruChild = this.getSyncedThruPath($child.id)
          this._animate($thruChild._color, [184, 192, 8, 100])
          this._animate($child._color, [184, 192, 8, 18])
        } else {
          this._animate($child._color, [184, 192, 8, this.highlightedSequenceAndNodeIndex ? 18 : 40])
        }
      }
    },
    deactivateChild ($child) {
      this.setLeave($child)
    },
    onAnimationUpdate () {
      this.requestDraw(true)
    },
    requestDraw (forced) {
      this._forceDraw = !!forced
      this._drawRequested = true
    },
    onCanvasClick (e) {
      if (this._$hoveredChild) {
        const indexs = this._$hoveredChild.id.split('-').map((v) => parseInt(v))
        this.$emit('onLineClick', e, indexs[1], indexs[2], indexs[3], this._$hoveredChild.hasThruPath)
      } else {
        this.$emit('onCloseInfoLayer')
      }
    },
    onCanvasPointerMove (e) {
      const rect = this._$canvas.getBoundingClientRect()
      const point = getEventPoint(e)
      const reversedScale = 1 / this.paneScale
      this._pointerX = multiplyPixelRatio(point[0] - rect.left) * reversedScale
      this._pointerY = multiplyPixelRatio(point[1] - rect.top) * reversedScale
      this.requestDraw()
    },
    onCanvasPointerLeave (e) {
      this._pointerX = this._pointerY = -999
      this.requestDraw()
    }
  }
}
</script>

<style lang="scss" scoped>
.canvas {
  position: absolute;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  &.hovered {
    cursor: pointer;
  }
}
</style>
